/**
 * CSV 导入对话框（student-registry-admin-ui:SC-11/SC-12，design 前端页面变更）。
 *
 * 三步式：上传 → 预览校验 → 完成。预览步骤提供「重传」入口返回上传步并清空已解析行。
 * 模板下载：生成 CSV 模板字符串供前端下载（data-testid 暴露）。
 * 科目分数列（subject-score-bands-and-scores SC-35）：`科目名称:分数` 分号分隔多科，
 * 服务端按机构内 ACTIVE 科目名逐科写入 student_subject_scores。
 * 预览表含非阻断提示列（SC-18 口径）：与已存档案同号时同名将跳过 / 异名将多孩建档。
 */
import { useState } from 'react';
import { Alert, Button, Steps, Table, Tag, Upload } from 'antd';
import { message } from '../../utils/antd-message';
import { DraggableModal } from '../../components/DraggableModal';
import { UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  studentsApi,
  type StudentImportRow,
} from '../../api/students.api';

const TEMPLATE =
  '姓名,性别,年级/学段,归属校区,监护人称呼,与孩子关系,监护人手机号,科目分数\n张三,男,小学低年级,高新校区,张先生,父亲,13800000001,数学:95;英语:88';

/** 供单测断言模板列名（与下载内容一致）。 */
export const STUDENT_IMPORT_CSV_TEMPLATE = TEMPLATE;

type Step = 'upload' | 'preview' | 'done';

export function StudentImportModal({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}) {
  const [step, setStep] = useState<Step>('upload');
  const [csv, setCsv] = useState('');
  const [rows, setRows] = useState<StudentImportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);

  function resetToUpload() {
    setStep('upload');
    setCsv('');
    setRows([]);
    setResult(null);
  }

  function downloadTemplate() {
    // UTF-8 BOM：Excel 中文版默认 GBK 打开 CSV 乱码，BOM 让 Excel 识别 UTF-8
    const blob = new Blob(['﻿' + TEMPLATE], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '学生导入模板.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleFile(file: File) {
    const text = await file.text();
    setCsv(text);
    setLoading(true);
    try {
      const preview = await studentsApi.previewImport(text);
      setRows(preview.rows);
      setStep('preview');
    } catch (err) {
      message.error(err instanceof Error ? err.message : '预览失败');
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm() {
    setLoading(true);
    try {
      const res = await studentsApi.confirmImport(csv);
      setResult(res);
      setStep('done');
      onImported();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '导入失败');
    } finally {
      setLoading(false);
    }
  }

  const columns: ColumnsType<StudentImportRow> = [
    { title: '行', dataIndex: 'rowNumber', key: 'rowNumber', width: 60 },
    { title: '姓名', dataIndex: 'name', key: 'name', width: 100 },
    { title: '手机号', dataIndex: 'guardianMobile', key: 'guardianMobile', width: 150 },
    {
      title: '科目分数',
      dataIndex: 'subjectScores',
      key: 'subjectScores',
      render: (_, r) =>
        r.subjectScores?.length
          ? r.subjectScores.map((s) => `${s.subjectName}:${s.score}`).join('；')
          : '—',
    },
    {
      title: '状态',
      key: 'valid',
      width: 100,
      render: (_, r) =>
        r.valid ? <Tag color="green">通过</Tag> : <Tag color="red">错误</Tag>,
    },
    {
      title: '错误',
      dataIndex: 'errors',
      key: 'errors',
      render: (errors: string[]) => errors.join('；'),
    },
    {
      title: '提示',
      dataIndex: 'hints',
      key: 'hints',
      render: (hints?: string[]) => (hints?.length ? hints.join('；') : '—'),
    },
  ];

  const stepIndex = step === 'upload' ? 0 : step === 'preview' ? 1 : 2;

  return (
    <DraggableModal
      title="导入学生"
      open={open}
      onCancel={onClose}
      destroyOnHidden
      footer={
        step === 'preview'
          ? [
              <Button key="retry" onClick={resetToUpload} data-testid="btn-reupload">
                重传
              </Button>,
              <Button key="cancel" onClick={onClose}>
                取消
              </Button>,
              <Button
                key="confirm"
                type="primary"
                loading={loading}
                onClick={handleConfirm}
                data-testid="btn-confirm-import"
              >
                确认导入
              </Button>,
            ]
          : step === 'done'
            ? [<Button key="close" type="primary" onClick={onClose}>完成</Button>]
            : null
      }
    >
      <Steps
        current={stepIndex}
        items={[{ title: '上传' }, { title: '预览校验' }, { title: '完成' }]}
        style={{ marginBottom: 16 }}
      />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="仅支持 CSV 文件（UTF-8），导入仅建立学生主档，不产生报名、不赠送课时"
        description="科目分数列格式：科目名称:分数，多科用分号分隔（如 数学:95;英语:88），科目须为机构内启用科目"
      />
      {step === 'upload' && (
        <>
          <Button icon={<UploadOutlined />} onClick={downloadTemplate} style={{ marginBottom: 12 }} data-testid="btn-download-template">
            下载模板
          </Button>
          <Upload.Dragger
            accept=".csv"
            showUploadList={false}
            beforeUpload={(file) => {
              void handleFile(file);
              return false;
            }}
            data-testid="upload-csv"
          >
            <p>点击或拖拽 CSV 文件到此区域上传</p>
          </Upload.Dragger>
        </>
      )}
      {step === 'preview' && (
        <Table
          scroll={{ x: 'max-content' }}
          rowKey="rowNumber"
          columns={columns}
          dataSource={rows}
          pagination={false}
          loading={loading}
          size="small"
          data-testid="preview-table"
        />
      )}
      {step === 'done' && result && (
        <Alert
          type="success"
          message={`成功 ${result.imported} 条，跳过 ${result.skipped} 条`}
          data-testid="import-result"
        />
      )}
    </DraggableModal>
  );
}
